import snapshot from '../data/vocabulary-snapshot.json'
import { UNCLASSIFIED, humanise, tidyName, readDimensionRefs } from './dimensions'
import { lastSegment } from './normalise'
import type { DimensionRef, Indicator } from './types'
import { MAX_REPORT_BYTES, fetchData, readResponseJson } from './limits'

/**
 * Resolving indicator and dimension definitions.
 *
 * A report names indicators by URI only. Everything readable about them -- the
 * name, the description, which quality dimensions they belong to -- lives in
 * the registry. Fetching that at runtime is what lets a newly published
 * indicator show up correctly with no release on our side, which is the whole
 * point of not hardcoding a catalogue.
 *
 * Three layers, in order:
 *
 *   1. A bundled snapshot, available immediately and offline.
 *   2. A cache of anything fetched since, so repeat visits stay cheap.
 *   3. The live registry, for anything the first two do not know.
 *
 * Nothing blocks on the network. An unresolved indicator still renders, using a
 * humanised slug, and is reported in the data quality view rather than hidden.
 */

export type VocabularySource = 'snapshot' | 'cache' | 'live' | 'unresolved'

interface SnapshotIndicator {
  uri: string
  slug: string
  name?: string
  description?: string
  alternateName?: string
  keywords?: string[]
  dimensions: string[]
  sources?: { name?: string; url?: string }[]
  status?: string
}

interface SnapshotDimension {
  uri: string
  slug: string
  name?: string
  description?: string
}

interface SnapshotFile {
  retrievedAt: string
  source: string
  indicators: SnapshotIndicator[]
  dimensions: SnapshotDimension[]
}

const CACHE_KEY = 'everse-vocabulary-cache-v1'
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000

/** Unknown definitions looked up per batch of reports, and how many at once. */
export const MAX_RESOLVED_AT_ONCE = 200
const RESOLVING_AT_ONCE = 6

/**
 * Content negotiation is required. Without this header the registry serves its
 * HTML website rather than the definition. The value is CORS-safelisted, so no
 * preflight is triggered.
 */
const LD_JSON = 'application/ld+json'

interface CacheFile {
  storedAt: number
  indicators: Record<string, SnapshotIndicator>
  dimensions: Record<string, SnapshotDimension>
}

export interface RegistryOptions {
  fetchImpl?: typeof fetch
  storage?: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
  /** Set false to work purely from the snapshot and cache. */
  allowNetwork?: boolean
}

/** Where EVERSE indicator definitions are published. */
const VOCABULARY_PREFIX = 'https://w3id.org/everse/'

/**
 * Whether a report-supplied address really points into the vocabulary.
 *
 * A prefix test alone passes `https://w3id.org/everse/../elsewhere`, which the
 * URL parser then resolves out of the vocabulary, so the parsed host and path
 * are checked and any dot segment, raw or percent-encoded, is refused. A
 * backslash counts as a separator because the parser treats it as one.
 */
function isVocabularyUri(uri: string): boolean {
  if (!uri.startsWith(VOCABULARY_PREFIX) || /(^|[/\\])(\.|%2e){1,2}([/\\?#]|$)/i.test(uri)) {
    return false
  }
  try {
    const url = new URL(uri)
    return (
      url.protocol === 'https:' && url.host === 'w3id.org' && url.pathname.startsWith('/everse/')
    )
  } catch {
    return false
  }
}

export class VocabularyRegistry {
  private readonly indicators = new Map<string, SnapshotIndicator>()
  private readonly dimensions = new Map<string, SnapshotDimension>()
  private readonly origin = new Map<string, VocabularySource>()
  private readonly inFlight = new Map<string, Promise<void>>()
  private readonly failed = new Set<string>()

  private readonly fetchImpl?: typeof fetch
  private readonly storage?: RegistryOptions['storage']
  private readonly allowNetwork: boolean

  constructor(options: RegistryOptions = {}) {
    this.fetchImpl =
      options.fetchImpl ?? (typeof fetch === 'function' ? fetch.bind(globalThis) : undefined)
    this.storage =
      options.storage ?? (typeof localStorage === 'undefined' ? undefined : localStorage)
    this.allowNetwork = options.allowNetwork ?? true

    const file = snapshot as SnapshotFile
    for (const dimension of file.dimensions) this.dimensions.set(dimension.slug, dimension)
    for (const indicator of file.indicators) {
      this.indicators.set(indicator.uri, indicator)
      this.origin.set(indicator.uri, 'snapshot')
    }

    this.loadCache()
  }

  /** When the bundled snapshot was taken, so the interface can show its age. */
  get snapshotDate(): string {
    return (snapshot as SnapshotFile).retrievedAt
  }

  /** Where a given definition came from. Surfaced in the data quality view. */
  sourceOf(uri: string): VocabularySource {
    return this.origin.get(uri) ?? 'unresolved'
  }

  /**
   * Always returns something usable. An indicator that cannot be resolved gets
   * a humanised slug and no dimensions, and says so via `resolved`.
   */
  get(uri: string): Indicator {
    const slug = lastSegment(uri)
    const found = this.indicators.get(uri)

    if (!found) {
      return {
        uri,
        slug,
        name: humanise(slug),
        dimensions: [],
        resolved: false,
      }
    }

    return {
      uri,
      slug: found.slug || slug,
      name: found.name ? tidyName(found.name) : humanise(slug),
      description: found.description,
      abbreviation: found.slug,
      alternateName: found.alternateName,
      keywords: found.keywords,
      dimensions: this.dimensionsOf(found),
      sources: found.sources,
      status: found.status,
      resolved: true,
    }
  }

  /** Dimensions an indicator belongs to, all of them, each filled in if known. */
  private dimensionsOf(indicator: SnapshotIndicator): DimensionRef[] {
    const refs = readDimensionRefs(indicator.dimensions)
    if (refs.length === 0) return []
    return refs.map((ref) => {
      const known = this.dimensions.get(ref.slug)
      return known
        ? {
            ...ref,
            name: known.name ? tidyName(known.name) : humanise(ref.slug),
            description: known.description,
          }
        : { ...ref, name: humanise(ref.slug) }
    })
  }

  /** Every indicator the registry knows, whether or not anything assessed it. */
  allIndicators(): Indicator[] {
    return [...this.indicators.keys()].map((uri) => this.get(uri))
  }

  /** Every published dimension. */
  allDimensions(): DimensionRef[] {
    return [...this.dimensions.values()].map((dimension) => ({
      uri: dimension.uri,
      slug: dimension.slug,
      name: dimension.name || humanise(dimension.slug),
      description: dimension.description,
    }))
  }

  dimension(slug: string): DimensionRef {
    if (slug === UNCLASSIFIED.slug) return UNCLASSIFIED
    const known = this.dimensions.get(slug)
    return known
      ? {
          uri: known.uri,
          slug,
          name: known.name ? tidyName(known.name) : humanise(slug),
          description: known.description,
        }
      : { uri: '', slug, name: humanise(slug) }
  }

  /**
   * Fetches anything not already known, in parallel, ignoring failures.
   *
   * Resolution is an enhancement: the dashboard is already usable without it,
   * so a registry that is unreachable degrades the labelling rather than
   * breaking the view.
   */
  async resolve(uris: Iterable<string>): Promise<void> {
    if (!this.allowNetwork || !this.fetchImpl) return

    const wanted = [...new Set(uris)].filter(
      (uri) => uri && !this.indicators.has(uri) && !this.failed.has(uri),
    )
    // A report is untrusted input. Fetching every URI it names would let one
    // make the reader's browser call any address, an intranet one or a
    // tracker, just by being opened. Only the vocabulary's own host is asked;
    // anything else is left unresolved and listed as such.
    for (const other of wanted.filter((u) => !isVocabularyUri(u))) this.failed.add(other)
    wanted.splice(0, wanted.length, ...wanted.filter(isVocabularyUri))
    // Real reports name tens of indicators. One that names thousands would
    // send as many requests from every reader's browser; past the cap they
    // stay unresolved, and a few at a time is all the host is asked.
    for (const other of wanted.splice(MAX_RESOLVED_AT_ONCE)) this.failed.add(other)
    if (wanted.length === 0) return

    let next = 0
    const worker = async () => {
      while (next < wanted.length) await this.resolveOne(wanted[next++]!)
    }
    await Promise.all(Array.from({ length: Math.min(RESOLVING_AT_ONCE, wanted.length) }, worker))
    this.saveCache()
  }

  private resolveOne(uri: string): Promise<void> {
    const existing = this.inFlight.get(uri)
    if (existing) return existing

    const task = (async () => {
      try {
        const response = await fetchData(uri, { headers: { accept: LD_JSON } }, this.fetchImpl)
        if (!response.ok) {
          this.failed.add(uri)
          return
        }
        const doc = (await readResponseJson(response, MAX_REPORT_BYTES)) as Record<string, unknown>
        this.indicators.set(uri, toSnapshotIndicator(uri, doc))
        this.origin.set(uri, 'live')
      } catch {
        // Offline, blocked, or the URI does not resolve. Recorded so we do not
        // retry it for every report in the session.
        this.failed.add(uri)
      } finally {
        this.inFlight.delete(uri)
      }
    })()

    this.inFlight.set(uri, task)
    return task
  }

  /**
   * The same registry under a new identity.
   *
   * Pages memoise on the registry, so labels resolved after they rendered
   * would never reach them. Handing out a fresh view of it whenever the
   * vocabulary changes makes every such memo recompute. It shares all state
   * with the original (nothing here reassigns a field after construction).
   */
  renewed(): VocabularyRegistry {
    return Object.create(this) as VocabularyRegistry
  }

  /** Forgets past failures, so the next resolve tries them again. */
  retryFailed(): void {
    this.failed.clear()
  }

  /** URIs tried and not resolved. Listed in the data quality view. */
  unresolved(): string[] {
    return [...this.failed].sort()
  }

  private loadCache(): void {
    if (!this.storage) return
    try {
      const raw = this.storage.getItem(CACHE_KEY)
      if (!raw) return
      const cache = JSON.parse(raw) as CacheFile
      if (Date.now() - cache.storedAt > CACHE_TTL_MS) {
        this.storage.removeItem(CACHE_KEY)
        return
      }
      for (const [slug, dimension] of Object.entries(cache.dimensions ?? {})) {
        if (!this.dimensions.has(slug)) this.dimensions.set(slug, dimension)
      }
      for (const [uri, indicator] of Object.entries(cache.indicators ?? {})) {
        if (!this.indicators.has(uri)) {
          this.indicators.set(uri, indicator)
          this.origin.set(uri, 'cache')
        }
      }
    } catch {
      // A corrupt or outgrown cache is not worth failing over; the snapshot
      // still covers the common case.
    }
  }

  private saveCache(): void {
    if (!this.storage) return
    const indicators: Record<string, SnapshotIndicator> = {}
    for (const [uri, indicator] of this.indicators) {
      if (this.origin.get(uri) !== 'snapshot') indicators[uri] = indicator
    }
    if (Object.keys(indicators).length === 0) return
    try {
      this.storage.setItem(
        CACHE_KEY,
        JSON.stringify({ storedAt: Date.now(), indicators, dimensions: {} } satisfies CacheFile),
      )
    } catch {
      // Quota exhausted. The cache is only an optimisation.
    }
  }
}

function toSnapshotIndicator(uri: string, doc: Record<string, unknown>): SnapshotIndicator {
  const dimensions = readDimensionRefs(doc.qualityDimension).map((ref) => ref.uri)
  return {
    uri,
    slug: asString(doc.abbreviation) ?? lastSegment(uri),
    name: asString(doc.name),
    description: asString(doc.description),
    alternateName: asString(doc.alternateName),
    keywords: Array.isArray(doc.keywords)
      ? doc.keywords.filter((k) => typeof k === 'string')
      : undefined,
    dimensions,
  }
}

function asString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined
}
